import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/dateUtils';
import './SharePage.css';

// 카카오톡 인앱 브라우저 감지
function isKakaoInApp() {
  return /KAKAOTALK/i.test(navigator.userAgent);
}

export default function SharePage({ token }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const [session, setSession] = useState(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const [showKakaoBanner, setShowKakaoBanner] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [myCategories, setMyCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);

  useEffect(() => {
    if (isKakaoInApp()) setShowKakaoBanner(true);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => setSession(s));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    async function fetchRecord() {
      try {
        const { data, error } = await supabase.rpc('get_shared_record', {
          p_token: token,
        });

        if (error) {
          setErrorMsg('기록을 불러올 수 없어요. 잠시 후 다시 시도해주세요.');
          setLoading(false);
          return;
        }

        if (!data) {
          setErrorMsg('링크가 만료되었거나 존재하지 않는 기록이에요.');
          setLoading(false);
          return;
        }

        setRecord(data);
        setLoading(false);
      } catch (e) {
        console.error('[SharePage] fetch error:', e);
        setErrorMsg('네트워크 오류가 발생했어요.');
        setLoading(false);
      }
    }
    fetchRecord();
  }, [token]);

  // 로그인된 사용자의 카테고리 불러오기
  useEffect(() => {
    if (!session?.user) return;
    supabase
      .from('categories')
      .select('*')
      .eq('user_id', session.user.id)
      .order('sort_order', { ascending: true })
      .then(({ data }) => setMyCategories(data || []));
  }, [session]);

  const openExternalBrowser = () => {
    const url = window.location.href;
    // 카카오톡 인앱 → 외부 브라우저 열기
    window.location.href = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(url);
  };

  // 카테고리 선택 화면 열기
  const handleStartImport = () => {
    // 원본 카테고리를 기본 선택으로
    setSelectedCat({
      name: record.category_name,
      icon: record.category_emoji || '📦',
    });
    setShowCategoryPicker(true);
  };

  // 선택된 카테고리로 실제 저장
  const handleImport = async () => {
    if (!session?.user || !record || !selectedCat) return;
    setImporting(true);

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: true })
      .limit(1);
    const profileId = profiles?.[0]?.id || null;

    const { data: newRecord, error: err } = await supabase.from('records').insert({
      user_id: session.user.id,
      profile_id: profileId,
      category_name: selectedCat.name,
      category_emoji: selectedCat.icon,
      title: record.title,
      start_date: record.start_date,
      end_date: record.end_date,
      is_ongoing: record.is_ongoing,
      memo: record.memo,
      photos: record.photos || [],
      location_name: record.location_name,
      latitude: record.latitude,
      longitude: record.longitude,
    }).select().single();

    if (err) {
      setImporting(false);
      alert('추가 실패: ' + err.message);
      return;
    }

    if (newRecord && profileId) {
      await supabase.from('record_shares').insert({
        record_id: newRecord.id,
        profile_id: profileId,
      });
    }

    setImporting(false);
    setImported(true);
    setShowCategoryPicker(false);
  };

  const handleLoginRedirect = () => {
    localStorage.setItem('lifelog_share_redirect', window.location.href);
    window.location.href = 'https://lifelog.mobile';
  };

  // ===== 로딩 =====
  if (loading) {
    return (
      <div className="sp">
        {showKakaoBanner && <KakaoBanner onOpen={openExternalBrowser} />}
        <div className="sp-loading">
          <div className="sp-loading-spinner" />
          <span>기록을 불러오는 중...</span>
        </div>
      </div>
    );
  }

  // ===== 에러 =====
  if (errorMsg || !record) {
    return (
      <div className="sp">
        {showKakaoBanner && <KakaoBanner onOpen={openExternalBrowser} />}
        <div className="sp-error-card">
          <span className="sp-error-icon">😢</span>
          <h2>기록을 찾을 수 없어요</h2>
          <p>{errorMsg || '링크가 만료되었거나 존재하지 않는 기록이에요.'}</p>
          <a href="https://lifelog.mobile" className="sp-btn sp-btn--primary">
            인생로그 시작하기
          </a>
        </div>
      </div>
    );
  }

  // ===== 정상 표시 =====
  const photos = Array.isArray(record.photos) ? record.photos : [];
  const emoji = record.category_emoji || '📦';
  const startFmt = formatDate(record.start_date);
  const dateText = record.is_ongoing
    ? `${startFmt} ~ 현재`
    : record.end_date
      ? `${startFmt} ~ ${formatDate(record.end_date)}`
      : startFmt;
  const isMyRecord = session?.user?.id === record.user_id;

  return (
    <div className="sp">
      {showKakaoBanner && <KakaoBanner onOpen={openExternalBrowser} />}

      <div className="sp-brand">
        <span className="sp-brand-logo">📖</span>
        <span className="sp-brand-name">인생로그</span>
      </div>

      <div className="sp-card">
        <div className="sp-card-emoji">{emoji}</div>
        <h1 className="sp-card-title">{record.title}</h1>
        <span className="sp-card-badge">{record.category_name}</span>

        <div className="sp-card-info">
          <div className="sp-info-row">
            <span className="sp-info-icon">📅</span>
            <span>{dateText}</span>
          </div>
          {record.location_name && (
            <div className="sp-info-row">
              <span className="sp-info-icon">📍</span>
              <span>{record.location_name}</span>
            </div>
          )}
        </div>

        {photos.length > 0 && (
          <div className="sp-photos">
            {photos.length === 1 ? (
              <img src={photos[0]} alt="사진" className="sp-photo sp-photo--single" onClick={() => setViewerPhoto(photos[0])} />
            ) : (
              <div className="sp-photo-grid">
                {photos.map((url, i) => (
                  <img key={i} src={url} alt={`사진 ${i + 1}`} className="sp-photo" onClick={() => setViewerPhoto(url)} />
                ))}
              </div>
            )}
          </div>
        )}

        {record.memo && (
          <div className="sp-memo">
            <div className="sp-memo-label">메모</div>
            <p>{record.memo}</p>
          </div>
        )}
      </div>

      <div className="sp-actions">
        {imported ? (
          <div className="sp-imported">
            <span>✅</span> 내 기록에 추가되었습니다!
          </div>
        ) : session ? (
          isMyRecord ? (
            <p className="sp-actions-sub">내가 공유한 기록이에요</p>
          ) : (
            <button className="sp-btn sp-btn--primary sp-btn--full" onClick={handleStartImport}>
              📥 내 기록에 추가하기
            </button>
          )
        ) : (
          <>
            <button className="sp-btn sp-btn--primary sp-btn--full" onClick={handleLoginRedirect}>
              📥 로그인하고 내 기록에 추가
            </button>
            <p className="sp-actions-sub">인생로그에 로그인하면 이 기록을 내 계정에 저장할 수 있어요</p>
          </>
        )}
      </div>

      <div className="sp-footer">
        <div className="sp-footer-divider" />
        <p className="sp-footer-text">나만의 소중한 기록을 남겨보세요</p>
        <a href="https://lifelog.mobile" className="sp-btn sp-btn--outline">
          나도 인생로그 시작하기
        </a>
      </div>

      {viewerPhoto && (
        <div className="sp-viewer" onClick={() => setViewerPhoto(null)}>
          <button className="sp-viewer-close" onClick={() => setViewerPhoto(null)}>✕</button>
          <img src={viewerPhoto} alt="크게 보기" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* 카테고리 선택 바텀시트 */}
      {showCategoryPicker && (
        <div className="sp-sheet-backdrop" onClick={() => setShowCategoryPicker(false)}>
          <div className="sp-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sp-sheet-header">
              <h3>어떤 카테고리에 저장할까요?</h3>
              <p className="sp-sheet-sub">원본 카테고리: {record.category_emoji} {record.category_name}</p>
            </div>

            <div className="sp-cat-list">
              {/* 원본 카테고리 (맨 위에 표시) */}
              <button
                className={`sp-cat-item ${selectedCat?.name === record.category_name ? 'sp-cat-item--active' : ''}`}
                onClick={() => setSelectedCat({ name: record.category_name, icon: record.category_emoji || '📦' })}
              >
                <span className="sp-cat-emoji">{record.category_emoji || '📦'}</span>
                <span className="sp-cat-name">{record.category_name}</span>
                <span className="sp-cat-tag">원본</span>
              </button>

              {/* 내 카테고리 목록 (원본과 겹치면 제외) */}
              {myCategories
                .filter((c) => c.name !== record.category_name)
                .map((cat) => (
                  <button
                    key={cat.id}
                    className={`sp-cat-item ${selectedCat?.name === cat.name ? 'sp-cat-item--active' : ''}`}
                    onClick={() => setSelectedCat({ name: cat.name, icon: cat.icon || '📦' })}
                  >
                    <span className="sp-cat-emoji">{cat.icon || '📦'}</span>
                    <span className="sp-cat-name">{cat.name}</span>
                  </button>
                ))}
            </div>

            <div className="sp-sheet-actions">
              <button
                className="sp-btn sp-btn--primary sp-btn--full"
                onClick={handleImport}
                disabled={importing || !selectedCat}
              >
                {importing ? '저장 중...' : `${selectedCat?.icon || ''} ${selectedCat?.name || ''} 카테고리에 저장`}
              </button>
              <button className="sp-sheet-cancel" onClick={() => setShowCategoryPicker(false)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KakaoBanner({ onOpen }) {
  return (
    <div className="sp-kakao-banner">
      <p>카카오톡 브라우저에서는 일부 기능이 제한될 수 있어요</p>
      <button className="sp-kakao-btn" onClick={onOpen}>
        외부 브라우저로 열기
      </button>
    </div>
  );
}
